<template>
  <div>
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <h1 class="title">Domain tracker</h1>
      <app-cells>
        <app-button @click.native="onAddButtonClick">Add domain</app-button>
        <app-input
          @input.native="onGroupsSearch"
          v-model="search"
          class="input--300"
          type="search"
          placeholder="Search by domain name"
          name="search"
        />
      </app-cells>
      <table-domains
        @updateDomain="onUpdateDomain"
        @editButtonClick="onEditButtonClick"
        @tableDomainDelete="onTableDomainDelete"
        @clickPagination="onClickPagination"
        :table-items="table_items"
        :table-preloader="table_preloader"
        :pagination="pagination"
      />
    </template>
    <modal
      name="domain-form"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('domain-form')" class="popup__close" type="button"></button>
        <div class="popup__title">{{ is_create ? 'Add' : 'Update' }} domain</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Domain name" label-for="domain_name" required>
              <app-input
                v-model="form.name"
                @change.native="$v.form.name.$touch()"
                :error="$v.form.name.$error"
                id="domain_name"
                placeholder="domain.zone"
                name="domain_name"
                :disabled="!is_create"
              />
              <template #error>
                <div v-if="$v.form.name.$dirty && !$v.form.name.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Contact list" required>
              <app-select
                v-model="form.notification_contacts"
                :options="contacts_options"
                :reduce="item => item.id"
                :clearable="false"
                :closeOnSelect="false"
                placeholder="Contact list"
                multiple
                :error="$v.form.notification_contacts.$error"
              />
              <template #error>
                <div v-if="$v.form.notification_contacts.$dirty && !$v.form.notification_contacts.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">{{ is_create ? 'Add' : 'Save' }}</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
    <modal
      name="delete-domain"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('delete-domain')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete Record
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to delete this domain?</div>
          <div v-if="this.current_domain" class="popup__info">{{ this.current_domain.name }}</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('delete-domain')" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteDomain" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { deleteDomain, getDomainContacts, getDomainTracker, getDomainUpdate, patchDomain, postDomain } from '@/http'
import { required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'
import TableDomains from '@/views/administrative/domains/tables/TableDomains'

export default {
  name: 'DomainsList',
  components: {TableDomains},
  data() {
    return {
      is_loaded: false,
      is_create: true,
      search: '',
      table_items: [],
      pagination: {
        pages: 1,
        page: 1,
        count: 1,
      },
      table_preloader: false,
      form: {
        name: '',
        notification_contacts: []
      },
      contacts_options: [],
      current_domain: null
    }
  },
  validations: {
    form: {
      name: { required },
      notification_contacts: { required },
    }
  },
  created() {
    this.fetchItems()
    getDomainContacts().then(res => {
      this.contacts_options = res.data
    })
  },
  methods: {
    fetchItems() {
      this.table_preloader = true
      getDomainTracker({ page: this.pagination.page, page_size: 20, search: this.search }).then(res => {
        this.table_items = res.data.results
        this.pagination.pages = res.data.pages
        this.pagination.count = res.data.count
        this.is_loaded = true
        this.table_preloader = false
      })
    },
    onClickPagination(page) {
      this.pagination.page = page
      this.fetchItems()
    },
    onCheckForm () {
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      if (this.is_create) {
        postDomain(this.form)
          .finally(() => { this.$refs.submit.preload = false })
          .then(() => {
            this.$notify({
              type: 'success',
              title: 'Domain created',
            })
            this.$modal.hide('domain-form')
            this.form = {}
            this.fetchItems()
          })
      } else {
        patchDomain(this.form.id, this.normalizeForm())
          .finally(() => { this.$refs.submit.preload = false })
          .then(() => {
            this.$notify({
              type: 'success',
              title: 'Domain saved',
            })
            this.$modal.hide('domain-form')
            this.form = {}
            this.fetchItems()
          })
      }
    },
    normalizeForm() {
      const normalizedForm = { ...this.form }

      if (normalizedForm.notification_contacts.every(item => typeof item === 'object')) {
        normalizedForm.notification_contacts = normalizedForm.notification_contacts.map(item => item.id)
      }

      return normalizedForm
    },

    onAddButtonClick() {
      this.form = {
        name: '',
        notification_contacts: []
      }
      this.$v.form.$reset()
      this.is_create = true
      this.$modal.show('domain-form')
    },
    onEditButtonClick(row) {
      this.form.id = row.id
      this.form.name = row.name
      this.form.notification_contacts = row.notification_contacts
      this.is_create = false
      this.$v.form.$reset()
      this.$modal.show('domain-form')
    },
    onUpdateDomain(id) {
      getDomainUpdate(id).then(() => {
        this.fetchItems()
        this.$notify({
          type: 'success',
          title: 'Domain data updated',
        })
      })
    },
    onTableDomainDelete(row) {
      this.current_domain = row
      this.$modal.show('delete-domain')
    },
    onDeleteDomain() {
      this.$refs.delete.preload = true
      deleteDomain(this.current_domain.id)
        .finally(() => { this.$refs.delete.preload = false })
        .then(() => {
          this.fetchItems()
          this.$notify({
            type: 'success',
            title: 'Domain deleted',
          })
          this.$modal.hide('delete-domain')
        })
    },
    onGroupsSearch() {
      this.searchGroups(this)
    },
    searchGroups: debounce(vm => {
      vm.pagination.page = 1
      vm.fetchItems()
    }, 350),
  }
}
</script>
